import { useMemo, useState } from 'react';

import './menu-item-options.scss';

import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { formatCurrency } from '../../../utils/currency/tools';
import { Button } from '../../common/button/Button';
import { AdaptiveModal } from '../AdaptiveModal';

const buildInitialSelections = (optionGroups, initialSelectedOptionIds) => {
  const selectedIds = new Set(initialSelectedOptionIds ?? []);
  const selections = {};

  (optionGroups ?? []).forEach((group) => {
    selections[group.id] = (group.options ?? [])
      .filter((option) => selectedIds.has(option.id))
      .map((option) => option.id);
  });

  return selections;
};

export const MenuItemOptionsModal = ({ open, onOpen, item, initialSelectedOptionIds, onConfirm }) => {
  const optionGroups = useMemo(() => item?.optionGroups ?? [], [item]);

  const [selections, setSelections] = useState(() =>
    buildInitialSelections(optionGroups, initialSelectedOptionIds)
  );

  const handleOpen = (isOpen) => {
    if (isOpen) {
      setSelections(buildInitialSelections(optionGroups, initialSelectedOptionIds));
    }
    onOpen(isOpen);
  };

  const selectSingle = (groupId, optionId) => {
    setSelections((prev) => ({ ...prev, [groupId]: [optionId] }));
  };

  const toggleMulti = (groupId, optionId) => {
    setSelections((prev) => {
      const current = prev[groupId] ?? [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      return { ...prev, [groupId]: next };
    });
  };

  const canConfirm = optionGroups.every(
    (group) => !group.required || (selections[group.id]?.length ?? 0) > 0
  );

  const handleConfirm = () => {
    if (!canConfirm) return;

    const selectedOptionIds = [];
    const selectedOptions = [];
    let priceDelta = 0;

    optionGroups.forEach((group) => {
      const chosenIds = selections[group.id] ?? [];
      if (chosenIds.length === 0) return;

      const chosenOptions = (group.options ?? []).filter((option) => chosenIds.includes(option.id));
      chosenOptions.forEach((option) => {
        selectedOptionIds.push(option.id);
        priceDelta += option.priceDelta ?? 0;
      });

      const labels = chosenOptions
        .map((option) => (option.priceDelta ? `${option.label} (+${formatCurrency(option.priceDelta)})` : option.label))
        .join(', ');
      selectedOptions.push(`${group.name}: ${labels}`);
    });

    onConfirm({ selectedOptionIds, selectedOptions, priceDelta });
    handleOpen(false);
  };

  if (!item) return null;

  return (
    <AdaptiveModal
      open={open}
      onOpen={handleOpen}
      contentStyle={{ minWidth: '320px', width: '420px', maxWidth: '96vw' }}
      mobileAutoHeight
      mobilePadded
    >
      <div className="menu-item-options">
        <div className="menu-item-options__name">{item.name}</div>
        <div className="menu-item-options__base-price">{formatCurrency(item.price)} base</div>

        {optionGroups.map((group) => (
          <div className="menu-item-options__group" key={group.id}>
            <div className="menu-item-options__group-head">
              <span>{group.name}</span>
              {group.required ? (
                <span className="menu-item-options__required">Required</span>
              ) : (
                <span className="menu-item-options__optional">Optional</span>
              )}
            </div>
            <div className="menu-item-options__option-list">
              {(group.options ?? []).map((option) => {
                const isMulti = group.selectionType === 'MULTI';
                const isSelected = (selections[group.id] ?? []).includes(option.id);
                const indicatorClass = [
                  'menu-item-options__indicator',
                  isMulti && 'menu-item-options__indicator--check',
                  isSelected && 'menu-item-options__indicator--selected'
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <label className="menu-item-options__option" key={option.id}>
                    <input
                      type={isMulti ? 'checkbox' : 'radio'}
                      name={group.id}
                      checked={isSelected}
                      onChange={() =>
                        isMulti ? toggleMulti(group.id, option.id) : selectSingle(group.id, option.id)
                      }
                    />
                    <span className={indicatorClass}>{isMulti && isSelected && <CheckIcon />}</span>
                    <span className="menu-item-options__option-label">{option.label}</span>
                    {option.priceDelta > 0 && (
                      <span className="menu-item-options__option-price">
                        +{formatCurrency(option.priceDelta)}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        <div className="menu-item-options__footer">
          <Button tertiary type="button" onClick={() => handleOpen(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canConfirm} onClick={handleConfirm}>
            Add to Cart
          </Button>
        </div>
      </div>
    </AdaptiveModal>
  );
};
